import { useEffect, useMemo, useState } from 'react';
import {
  useHealthcareServices,
  useOrganizations,
  useServiceRequests,
  useTasks,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import useRegistry from 'src/careflow/ph-core/multi-tenant/registry/useRegistry';
import {
  ServiceRequestWrapper,
  WrappedServiceRequest,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { Box, Button, Card, Chip, Grid, Stack, SxProps } from '@mui/material';
import { NoteAdd as NoteAddIcon, Work, Workspaces } from '@mui/icons-material';
import { getIdFromReference } from 'src/utils/fhir';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import {
  PatientGenericServiceRequestDto,
  wrappedServiceRequestToPatientGenericServiceRequestDto,
} from './patientGenericServiceRequest.dto';
import { HealthcareServiceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import AddReferral from 'src/careflow/care-coordination/inbound-referral/AddReferral';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import ModalCommunication from 'src/careflow/common/ModalCommunication';
import OutboundReferralDetailDrawer from 'src/careflow/care-coordination/referral/components/OutboundReferralDetailDrawer';
import ServiceRequestActionMenu from './ServiceRequestActionMenu';
import CollapsibleDataGrid from 'src/careflow/ph-core/grid/react/CollapsibleDataGrid';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { GridColDef } from '@mui/x-data-grid';
import CustomLink from 'src/careflow/common/CustomLink';
import CellRow from 'src/careflow/common/CellRow';
import moment from 'moment';
import { PermissionsBase, PermissionsProvider } from 'src/careflow/ph-core/admin/permissions/context/PermissionsContext';
import CaseTaskGrid from 'src/careflow/care-coordination/case/detail/components/CaseTaskGrid';
import { TaskWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { TaskPermissions } from 'src/careflow/helpers/types';
import ActionButton from 'src/components/ActionButton';
import { NestedMenuItem } from 'mui-nested-menu';
import {
  getDisplay,
  getReferred,
} from 'src/careflow/care-coordination/referral/components/workflow-step/ReferralInformation';
import {
  Reference,
  Task,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import Iconify from 'src/components/Iconify';
import AddRequest from 'src/careflow/inck/nat-request/AddRequest';
import PatientGenericServiceRequestCategorySelector from './PatientGenericServiceRequestCategorySelector';
import { capitalize, isNumber } from 'lodash';
import { getCategoryChipStyle } from 'src/careflow/care-coordination/referral/utils/categoryUtils';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { filterTasksByDefinition, getAssessmentFromTask, getAssessmentUrlWithUserId, isAssessmentReadyToStart } from 'src/careflow/helpers/common';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { iconGreyFillColor } from 'src/careflow/helpers/constants';
import AssessmentFrame from 'src/careflow/care-coordination/patient/assessment/frame/AssessmentFrame';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { useSnackbar } from 'notistack';
import GoalConditionList from '../../case/goal/grid/GoalConditionList';
import { commonMenuTaskHandlers } from 'src/careflow/care-coordination/task/common/handlers';
import TaskContextProvider from 'src/careflow/care-coordination/task/common/provider';
import TaskModal from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';

type Props = {
  patient?: WrappedPatient | null;
  carePlan?: WrappedCarePlan | null;
  refreshExternal?: boolean;
  permissions: any | null;
  taskActivityDefinitionContext?: string[];
  planDefinition?: string;
  conditions?: WrappedCondition[];
  observations?: WrappedObservation[];
};

export interface ServiceRequestsPermissions extends PermissionsBase {
  isAllowedToAdd: boolean;
  isAllowedToEdit: boolean;
  isAllowedToViewNotes: boolean;
  taskPermissions: TaskPermissions;
}

export default function PatientGenericServiceRequest({ 
  patient,
  carePlan,
  refreshExternal,
  permissions,
  taskActivityDefinitionContext,
  planDefinition,
  conditions,
  observations,
}: Props) {
  const user = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { configurations } = useTenantConfigData();
  const { taskPermissions } = permissions;
  const [openAdd, setOpenAdd] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [serviceRequestToEdit, setServiceRequestToEdit] = useState<WrappedServiceRequest | null>(
    null
  );
  const [openSelectRequestCategory, setOpenSelectRequestCategory] = useState(false);
  const [openAddRequest, setOpenAddRequest] = useState(false);
  const [isReferralDrawerOpen, setIsReferralDrawerOpen] = useState(false);
  const [serviceRequestMap, setServiceRequestMap] = useState<WrappedServiceRequest[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [openAssessmentFrame, setOpenAssessmentFrame] = useState(false);
  const [selectedTaskAssessmentId, setSelectedTaskAssessmentId] = useState<null | string>(null);
  const [selectedTaskAssessmentUrl, setSelectedTaskAssessmentUrl] = useState<null | string>(null);

  // Replace valueset with registry for referral types configuration
  const { data: registry } = useRegistry({ 
    filter: { keyRegistry: 'referral-types-config' } 
  });

  const requestCategoriesList = useMemo(() => {
    if (!registry?.[0]?.keyValue) return [];
    
    try {
      const config = JSON.parse(registry[0].keyValue);
      return config.referralTypes?.map((type: any) => ({
        code: type.code,
        display: type.display,
        system: 'referral-types-config'
      })) ?? [];
    } catch (error) {
      return [];
    }
  }, [registry]);

  const [
    serviceRequests,
    { refresh: refreshServiceRequests, isFetching: isServiceRequestLoading },
  ] = useServiceRequests({
    filter: { 
      patient: patient?.id,
      ...(carePlan?.id && { 'based-on': carePlan?.id })
    },
    autofetch: !!patient,
    map: ServiceRequestWrapper,
  });

  const serviceRequestIds = useMemo(() => {
    if (!serviceRequests?.length) return [];
    return [
      ...serviceRequests.reduce<Set<string>>((ids, { id }) => {
        if (!id) return ids;
        ids.add(`ServiceRequest/${id}`);
        return ids;
      }, new Set()),
    ];
  }, [serviceRequests]);

  const [tasks, { refresh: refreshTaskList }] = useTasks({
    filter: {
      subject: patient?.id,
      'based-on': serviceRequestIds.join(),
    },
    autofetch: !!serviceRequestIds.length,
    map: TaskWrapper,
  });

  const taskFilterByActivityDefinition = useMemo(() => {
    return filterTasksByDefinition(tasks, taskActivityDefinitionContext);
  }, [tasks, taskActivityDefinitionContext]);

  const [organizationIds, setOrganizationIds] = useState<string[]>([]);

  const { i18n } = useLocales();

  const references = useMemo(() => {
    const perf = serviceRequests
      .filter((s) => s?.performer)
      ?.map((s) => s?.performer)
      ?.flat();

    const req = serviceRequests
      .filter((s) => s?.requester)
      ?.map((s) => s?.requester)
      ?.flat();

    const uniqueCombinedRefs = [
      ...new Map([...perf, ...req].map((x) => [x?.reference, x])).values(),
    ];

    return uniqueCombinedRefs;
  }, [serviceRequests]);

  const healthcareServiceIds = useMemo(() => {
    if (references) {
      const organizationRefs = references.filter((x) =>
        x?.reference?.startsWith('HealthcareService')
      );
      if (organizationRefs) {
        return organizationRefs.map((x) =>
          x?.reference ? getIdFromReference(x?.reference) : null
        );
      }
    }

    return [];
  }, [references]);

  const [organizations, { isFetching: isOrganizationsLoading }] =
    useOrganizations({
      filter: {
        _id: organizationIds?.join(',') ?? null,
      },
      autofetch: !!organizationIds?.join(','),
      map: OrganizationWrapper,
    });

  const [healthcareServices, { isFetching: isHealthCareLoading }] = useHealthcareServices({
    filter: {
      _id: healthcareServiceIds?.join(',') ?? null,
    },
    autofetch: !!healthcareServiceIds?.join(','),
    map: HealthcareServiceWrapper,
  });

  useEffect(() => {
    if (!healthcareServices?.length) return;
    const organizationIds = [
      ...healthcareServices.reduce<Set<string>>((organizationIds, healthCareService) => {
        const organizationId = healthCareService?.providedBy?.reference?.split?.('/')?.[1];
        if (organizationId) organizationIds.add(organizationId);
        return organizationIds;
      }, new Set()),
    ];
    setOrganizationIds(organizationIds);
  }, [healthcareServices]);

  const getReferredFromServiceRequest = async () => {
    const serviceRequestMapped = Promise.all(
      serviceRequests?.map(async (e: WrappedServiceRequest) => {
        // If display is already available, use it without making an API call
        if (e?.requester?.display && e?.requester?.reference) {
          return {
            ...e,
            requester: {
              reference: e.requester.reference,
              display: e.requester.display,
            },
          };
        }
        
        // Only make API call if display is missing
        const { referred } = await getReferred(e);
        const stringReferred = referred ? getDisplay(referred) : (e?.requester?.display || '');
        const referenceReferred: Reference = {
          reference: `${referred?.resourceType}/${referred?.id}` || e?.requester?.reference || '',
          display: stringReferred,
        };
        return {
          ...e,
          requester: referenceReferred,
        };
      })
    );
    return serviceRequestMapped;
  };

  useEffect(() => {
    if (!serviceRequests?.length) {
      setServiceRequestMap([]);
      return;
    }
    
    setIsLoading(true);
    const fetchData = async () => {
      const data = await getReferredFromServiceRequest();
      setServiceRequestMap(data);
      setIsLoading(false);
    };
    fetchData();
  }, [serviceRequests]);

  const data = useMemo(() => {
    if (!serviceRequestMap?.length) return [];

    let filterByPlanDefinition: any = serviceRequestMap;

    if(planDefinition && planDefinition?.length){
      filterByPlanDefinition = serviceRequestMap?.filter(srm => 
        srm?.instantiatesCanonical?.some((ic) => planDefinition?.includes(ic))
      );
    }

    return filterByPlanDefinition?.map((x) =>
      wrappedServiceRequestToPatientGenericServiceRequestDto(
        x,
        organizations ?? [],
        healthcareServices ?? [],
        taskFilterByActivityDefinition ?? [],
        observations ?? [],
        conditions ?? [],
        configurations?.fhir,
      )
    );
  }, [
    serviceRequestMap, 
    organizations, 
    taskFilterByActivityDefinition, 
    healthcareServices, 
    configurations, 
    planDefinition,
    observations,
    conditions
  ]);

  const handleClose = () => {
    setOpenAdd(false);
    setServiceRequestToEdit(null);
  };

  const handleCategorySelect = (selectedCategory?: ValueSetComposeIncludeConcept) => {
    if (!selectedCategory) {
      setOpenAdd(true);
      return;
    }
    if (selectedCategory?.code === 'inck') {
      setOpenAddRequest(true);
    } else {
      setOpenAdd(true);
    }
  };

  const handleOpenServiceRequest = () => {
    if (!requestCategoriesList) {
      return;
    }
    if (requestCategoriesList?.length === 0) {
      setOpenAdd(true);
    } else if (requestCategoriesList?.length === 1) {
      handleCategorySelect(requestCategoriesList[0]);
    } else if (requestCategoriesList?.length > 1) {
      setOpenSelectRequestCategory(true);
    }
    setServiceRequestToEdit(null);
  };

  const columns: GridColDef<PatientGenericServiceRequestDto>[] = [
    {
      flex: 1,
      sortable: false,
      field: 'internalNumber',
      headerName: i18n('patients.details.serviceRequests.internalNumber', 'crs'),
      renderCell: (params) => {
        const { row } = params;

        const serviceRequest = serviceRequests?.find?.(({ id }) => id === row.id);

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <CustomLink
              to="#"
              onClick={() => {
                if (!serviceRequest) return;
                setIsReferralDrawerOpen(true);
                setServiceRequestToEdit(serviceRequest);
              }}
            >
              {row?.internalNumber}
            </CustomLink>
          </Box>
        );
      },
    },
    {
      flex: 0.8,
      sortable: false,
      field: 'created',
      headerName: i18n('patients.details.serviceRequests.created', 'crs'),
      renderCell: (params) => {
        const row = params.row;

        return (
          <CellRow
            shouldTruncateText={false}
            title={
              moment(row?.created ?? null).isValid()
                ? moment(row?.created).format('MM/DD/YYYY')
                : null
            }
          />
        );
      },
    },
    {
      flex: 1,
      sortable: false,
      field: 'referredFromName',
      headerName: i18n('patients.details.serviceRequests.referredFrom', 'crs'),
      renderCell: (params) => {
        const { row } = params;

        return <CellRow shouldTruncateText={false} title={row?.referredFromName} />;
      },
    },
    {
      flex: 1,
      sortable: false,
      field: 'serviceName',
      headerName: i18n('patients.details.serviceRequests.service', 'crs'),
      renderCell: (params) => {
        const { row } = params;
        return (
          <Box>
            <CellRow
              tooltipTitle={i18n('patients.details.serviceRequests.service', 'crs')}
              shouldTruncateText={false} 
              title={row?.serviceName}
              Icon={<Work sx={{ height: 15, width: 15 }} htmlColor={iconGreyFillColor}/>}
            />
            <CellRow 
              tooltipTitle={i18n('patients.details.serviceRequests.providedBy', 'crs')}
              shouldTruncateText={false} 
              title={row?.serviceOrganizationName}
              Icon={<Workspaces sx={{ height: 15, width: 15 }} htmlColor={iconGreyFillColor}/>}
            />
          </Box>
        );
      },
    },
    {
      flex: 0.8,
      sortable: false,
      field: 'status',
      headerName: i18n('patients.details.serviceRequests.status', 'crs'),
      renderCell: (params) => {
        const { row } = params;

        return <CellRow shouldTruncateText={false} title={capitalize(row?.status)} />;
      },
    },
    {
      flex: 1,
      sortable: false,
      field: 'conditions',
      headerName: `${i18n('patients.details.serviceRequests.condition', 'crs')}`,
      renderCell: (params) => (
        <GoalConditionList
          observations={params.row.observations}
          conditions={params.row.conditions ?? []}
        />
      ),
    },
    {
      flex: 1,
      sortable: false,
      field: 'category',
      headerName: 'Category',
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}> 
            {row?.category?.map(({ coding }) => (
              <Chip
                clickable={false}
                size="small"
                key={coding?.[0]?.code}
                label={coding?.[0]?.display}
                sx={getCategoryChipStyle({ coding })}
              />
            ))}
          </Box>
        );
      },
    },
    {
      flex: 0.5,
      sortable: false,
      field: 'action',
      headerName: ' ',
      renderCell: (params) => {
        const { row } = params;
        const serviceRequest = serviceRequests?.find?.(({ id }) => id === row.id);

        return (
          <ServiceRequestActionMenu
            serviceRequest={serviceRequest}
            canEdit={!!permissions.isAllowedToEdit}
            canAddTask={!!taskPermissions.isAllowedToAdd}
            labels={{
              notes: i18n('patients.details.serviceRequests.notes', 'crs'),
              edit: i18n('patients.details.serviceRequests.edit', 'crs'),
              task: i18n('tasks.task', 'crs'),
            }}
            onNotesOpen={() => {
              if (!serviceRequest) return;
              setIsNotesModalOpen(true);
              setServiceRequestToEdit(serviceRequest);
            }}
            onEdit={() => {
              if (!serviceRequest) return;
              setOpenAdd(true);
              setServiceRequestToEdit(serviceRequest);
            }}
            onAddTask={() => {
              if (!serviceRequest) return;
              setIsTaskModalOpen(true);
              setServiceRequestToEdit(serviceRequest);
            }}
          />
        );
      },
    }
  ];

  useEffect(() => {
    if (refreshExternal) refreshServiceRequests();
  }, [refreshExternal]);

  const handleAssessmentSelect = async (task: Task) => {
    if (!task.id) return;

    const inputAssessment = getAssessmentFromTask(task);
    if (!inputAssessment) return;

    if (isAssessmentReadyToStart(inputAssessment)) {
      setSelectedTaskAssessmentId(task.id);
    } else {
      await refreshTaskList();
      setSelectedTaskAssessmentId(task.id);
    }
  };

  const selectedTaskAssessment = useMemo(
    () => tasks.find((t) => t?.id === selectedTaskAssessmentId),
    [tasks, selectedTaskAssessmentId]
  );

  useEffect(() => {
    if (selectedTaskAssessment) {
      openOrUpdateAssessment();
    }
  }, [selectedTaskAssessment, tasks]);

  const openOrUpdateAssessment = () => {
    if (!selectedTaskAssessment) return;

    const inputAssessment = getAssessmentFromTask(selectedTaskAssessment);
    if (!inputAssessment) return;

    if (isAssessmentReadyToStart(inputAssessment)) {
      const assessmentUrl = getAssessmentUrlWithUserId(
        inputAssessment,
        user?.getCurrentUser?.()?.user_fhir_uri,
        { taskId: selectedTaskAssessment?.id, patientId: patient?.id }
      );
      setSelectedTaskAssessmentUrl(assessmentUrl ?? null);
      setOpenAssessmentFrame(true);
    } else {
      enqueueSnackbar('Preparing assessment. Please try again later.', {
        variant: 'error',
      });
    }
    setSelectedTaskAssessmentId(null);
  };

  const onCancel = () => {
    setOpenAssessmentFrame(false);
    setSelectedTaskAssessmentId(null);
    setSelectedTaskAssessmentUrl(null);
  };

  return (
    <PermissionsProvider permissions={permissions}>
      <Card
        style={{
          boxShadow: 'none',
          position: 'static',
        }}
        sx={{ m: 1 }}
      >
        <Grid container>
          <Grid item xs={12}>
            <CollapsibleDataGrid
              subGridRowsExtractor={({ tasks }: PatientGenericServiceRequestDto) => tasks ?? []}
              renderCollapsibleContent={({ id, status, internalNumber, tasks }: PatientGenericServiceRequestDto) => (
                <PermissionsProvider permissions={taskPermissions}>
                  <TaskContextProvider
                    basedOn={[{ 
                      reference: `ServiceRequest/${id}`,
                      display: status,
                      identifier: { value: internalNumber }
                    }]}
                    menuTaskHandlers={commonMenuTaskHandlers}
                    taskActivityDefinitionContext={taskActivityDefinitionContext}
                  >
                    <CaseTaskGrid
                      patient={patient ?? null}
                      tasks={tasks ?? []}
                      carePlan={carePlan}
                      serviceRequests={serviceRequests}
                      includeSubTasks={false}
                      isNestedGrid={true}
                      showPagination={false}
                      title={i18n('patients.details.tasks.title', 'crs')}
                      buttonTitle={i18n('patients.details.tasks.button', 'crs')}
                      onAssessmentSelect={handleAssessmentSelect}
                    />
                  </TaskContextProvider>
                </PermissionsProvider>
              )}
              getRowSpacing={() => ({ bottom: 1, top: 2 })}
              dataGridSx={{
                '& .MuiDataGrid-row': {
                  borderBottom: '1px solid #EAECF0',
                },
                '& .MuiDataGrid-row:nth-of-type(odd)': {
                  backgroundColor: '#FAFAFA',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#F5F6F7',
                },
              }}
              addButtonTitle={i18n('patients.details.serviceRequests.newServiceRequest', 'crs')}
              onAddButtonClick={handleOpenServiceRequest}
              showAddButton={false}
              density="standard"
              loading={
                isServiceRequestLoading ||
                isOrganizationsLoading ||
                isHealthCareLoading ||
                isLoading
              }
              getRowHeight={() => 'auto'}
              onFilterDrawerOpen={() => {}}
              rows={data}
              columns={columns}
              getRowId={({ id }: PatientGenericServiceRequestDto) => id as string}
              showPagination={true}
              childrenButtons={
                <Grid item xs={12} display={'flex'} justifyContent={'flex-end'} marginRight={2}>
                  {isNumber(requestCategoriesList?.length) &&
                  requestCategoriesList?.length === 0 ? (
                    <Button
                      variant="contained"
                      sx={{
                        height: '100%',
                        minWidth: '64px',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        left: '2%',
                      }}
                      startIcon={<Iconify icon={'eva:plus-fill'} />}
                      onClick={() => handleCategorySelect()}
                      data-name="service-request-button"
                    >
                      {i18n('patients.details.serviceRequests.newServiceRequest', 'crs')}
                    </Button>
                  ) : null}

                  {isNumber(requestCategoriesList?.length) &&
                  requestCategoriesList?.length === 1 ? (
                    <Button
                      variant="contained"
                      sx={{
                        height: '100%',
                        minWidth: '64px',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        left: '2%',
                      }}
                      startIcon={<Iconify icon={'eva:plus-fill'} />}
                      onClick={() => handleCategorySelect(requestCategoriesList[0])}
                      data-name="service-request-button"
                    >
                      {i18n('patients.details.serviceRequests.newServiceRequest', 'crs')}
                    </Button>
                  ) : null}

                  {isNumber(requestCategoriesList?.length) && requestCategoriesList?.length > 1 ? (
                    <ActionButton
                      renderChildren={(isMenuOpen, setIsMenuOpen) => (
                        <>
                          {requestCategoriesList.map((cat) => (
                            <NestedMenuItem
                              key={cat.code}
                              sx={nestedMenuItemStyles}
                              onClick={() => {
                                handleCategorySelect(cat);
                                setIsMenuOpen(false);
                              }}
                              leftIcon={<NoteAddIcon />}
                              rightIcon={null}
                              label={cat?.display}
                              parentMenuOpen={isMenuOpen}
                            />
                          ))}
                        </>
                      )}
                      customButton={(isMenuOpen, setIsMenuOpen, onClick, ref) => (
                        <Button
                          variant="contained"
                          sx={{
                            height: '100%',
                            minWidth: '64px',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            left: '2%',
                          }}
                          startIcon={<Iconify icon={'eva:plus-fill'} />}
                          onClick={onClick}
                          ref={ref}
                          data-name="service-request-button"
                        >
                          {i18n('patients.details.serviceRequests.newServiceRequest', 'crs')}
                        </Button>
                      )}
                      useCustomButton={true}
                    />
                  ) : null}
                </Grid>
              }
            />
          </Grid>
        </Grid>
        <AddReferral
          title={i18n('patients.details.serviceRequests.titleModal', 'crs')}
          openAdd={openAdd}
          referral={serviceRequestToEdit}
          carePlan={carePlan}
          onReferralSuccess={refreshServiceRequests}
          patient={patient}
          onCancel={handleClose}
          observations={observations ?? []}
          conditions={conditions ?? []}
        />
        <ModalCommunication
          isAllowedToAdd={checkAclValidation({
            acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.NOTES.ADD],
          })}
          open={isNotesModalOpen}
          patient={patient ?? null}
          onCancel={() => {
            setIsNotesModalOpen(false);
          }}
          resource={serviceRequestToEdit}
          typeNote="notes_referral"
          i18n={i18n}
        />
      </Card>
      <OutboundReferralDetailDrawer
        open={isReferralDrawerOpen}
        actionButtonProps={{
          onClick: () => {
            setIsReferralDrawerOpen(false);
            setOpenAdd(true);
          },
        }}
        serviceRequest={serviceRequestToEdit}
        onCloseIconButtonClick={() => {
          setIsReferralDrawerOpen(false);
        }}
      />
      <TaskContextProvider 
        basedOn={[{ 
          reference: `ServiceRequest/${serviceRequestToEdit?.id}`,
          display: serviceRequestToEdit?.status ?? "",
          identifier: { 
            value: serviceRequestToEdit
              ?.getInternalNumber(configurations?.fhir?.serviceRequestInternalNumberIdenitifierSystem) 
              ?? "" 
          }
        }]}
        taskActivityDefinitionContext={taskActivityDefinitionContext}
      >
        <TaskModal
          onSuccessfulCreation={refreshTaskList}
          onSuccessfulEdit={refreshTaskList}
          patientExternal={patient}
          carePlan={carePlan}
          open={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
        />
      </TaskContextProvider>
      <AddRequest
        open={openAddRequest}
        patient={patient ?? null}
        handleRefresh={() => {
          refreshServiceRequests();
          refreshTaskList();
        }}
        onCancel={() => setOpenAddRequest(false)}
      />
      <PatientGenericServiceRequestCategorySelector
        open={openSelectRequestCategory}
        categories={requestCategoriesList}
        onSelect={handleCategorySelect}
        // patient={patient ?? null}
        // handleRefresh={() => {
        //   refreshServiceRequests();
        //   refreshTaskList();
        // }}
        onCancel={() => setOpenSelectRequestCategory(false)}
      />

      {selectedTaskAssessmentUrl && openAssessmentFrame ? (
        <AssessmentFrame
          open={openAssessmentFrame}
          url={selectedTaskAssessmentUrl}
          onCancel={onCancel}
        />
      ) : null}
    </PermissionsProvider>
  );
}

const nestedMenuItemStyles: SxProps = {
  paddingX: 3,
};